import { useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useWorkspaceStore, getPersistedWorkspaceId } from '@/shared/stores/workspace.store';
import type { Workspace } from '@/shared/stores/workspace.store';
import { api } from '@/shared/services/api';
import { useAuthStore } from '@/shared/stores/auth.store';
import { workspaceService, type CreateWorkspaceInput, type DeleteWorkspaceResult, type UpdateWorkspaceInput } from '@/shared/services/workspace.service';

const WORKSPACES_QUERY_KEY = ['workspaces', 'me'] as const;

/**
 * Loads the user's workspaces. Backed by React Query so multiple
 * consumers (Sidebar, AppLayout, Dashboard, Inbox) share a single
 * in-flight request and a single cache entry — previously each
 * mount fired its own GET /workspaces/me, and a bad `currentWorkspace`
 * dependency in the effect caused refetch loops.
 */
export function useWorkspace() {
  const { workspaces, currentWorkspace, setWorkspaces, setCurrentWorkspace } = useWorkspaceStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: WORKSPACES_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<Workspace[]>('/workspaces/me');
      return res.data;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });

  // Sync the response into the Zustand store so non-React-Query consumers
  // (e.g., the api.ts request interceptor reading workspace id) stay in sync.
  useEffect(() => {
    if (!data) return;
    setWorkspaces(data);
    // Re-pick when the selected workspace isn't in the list any more — it
    // belonged to a previously signed-in account, or the user was removed
    // from it. Keeping it meant every request 403'd in WorkspaceGuard.
    //
    // On a reload the store is empty: the brand the person last had open
    // (persisted id) is picked, not the first one, or an agency user in
    // brand B was moved to brand A by every F5.
    //
    // When it is still in the list, the fresh row replaces the stored one,
    // so a rename, a role change or a new subscription shows at once.
    const current = useWorkspaceStore.getState().currentWorkspace;
    const fresh = current ? data.find((w) => w.id === current.id) : undefined;
    if (fresh) {
      if (fresh !== current) setCurrentWorkspace(fresh);
    } else if (data.length > 0) {
      const persistedId = getPersistedWorkspaceId();
      setCurrentWorkspace(data.find((w) => w.id === persistedId) ?? data[0]!);
    }
    // The current brand is read through getState(), not a dependency:
    // depending on it looped (setting it re-ran the effect that set it).
  }, [data, setWorkspaces, setCurrentWorkspace]);

  const switchWorkspace = useCallback(
    (workspace: Workspace) => {
      const previousId = useWorkspaceStore.getState().currentWorkspace?.id;
      setCurrentWorkspace(workspace);
      if (previousId === workspace.id) return;
      // Most keys carry the workspace id, but not all (a post or template by
      // id, credits capabilities…). Mark everything but the workspace list
      // stale so nothing from the previous brand is shown under the new one.
      void queryClient.invalidateQueries({
        predicate: (q) => !(q.queryKey[0] === WORKSPACES_QUERY_KEY[0] && q.queryKey[1] === WORKSPACES_QUERY_KEY[1]),
      });
    },
    [setCurrentWorkspace, queryClient],
  );

  return {
    workspaces,
    currentWorkspace,
    isLoading,
    switchWorkspace,
  };
}

/**
 * "Add brand" (blueprint M20): creates a workspace, appends it to the store and
 * makes it current so the whole app re-scopes at once, then invalidates the
 * list so the next read carries the server's ordering and counts.
 */
export function useCreateWorkspace(): UseMutationResult<Workspace, Error, CreateWorkspaceInput> {
  const queryClient = useQueryClient();
  return useMutation<Workspace, Error, CreateWorkspaceInput>({
    mutationFn: (input: CreateWorkspaceInput) => workspaceService.create(input),
    onSuccess: (created) => {
      const { workspaces, setWorkspaces, setCurrentWorkspace } = useWorkspaceStore.getState();
      setWorkspaces([...workspaces.filter((w) => w.id !== created.id), created]);
      setCurrentWorkspace(created);
      queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
      // The account's brand count, and on Agency its monthly charge, changed.
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Deletes a brand (ADR-006). On success the brand leaves the store, another
 * brand becomes current when the deleted one was, and every query is marked
 * stale: the brand count, the account's monthly charge and anything cached
 * under the old brand all change.
 */
export function useDeleteWorkspace(): UseMutationResult<DeleteWorkspaceResult, Error, { workspaceId: string; confirmName: string }> {
  const queryClient = useQueryClient();
  return useMutation<DeleteWorkspaceResult, Error, { workspaceId: string; confirmName: string }>({
    mutationFn: ({ workspaceId, confirmName }) => workspaceService.remove(workspaceId, confirmName),
    onSuccess: (_void, { workspaceId }) => {
      const { workspaces, currentWorkspace, setWorkspaces, setCurrentWorkspace } = useWorkspaceStore.getState();
      const remaining = workspaces.filter((w) => w.id !== workspaceId);
      setWorkspaces(remaining);
      const next = remaining[0];
      if (currentWorkspace?.id === workspaceId) {
        // The last brand gone: nothing is current, rather than the deleted one.
        if (next) setCurrentWorkspace(next);
        else useWorkspaceStore.getState().reset();
      }
      queryClient.setQueryData<Workspace[]>(WORKSPACES_QUERY_KEY, (old) => old?.filter((w) => w.id !== workspaceId));
      void queryClient.invalidateQueries();
    },
  });
}

/** Transfer ownership of the current workspace to another member. */
export function useTransferWorkspaceOwnership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ newOwnerId }: { newOwnerId: string }) => workspaceService.transferOwnership(newOwnerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
    },
  });
}

export function useCancelTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => workspaceService.cancelTransfer(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
    },
  });
}

/** The recipient accepts or declines a transfer; accepting moves the brand between accounts. */
export function useAnswerTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accept }: { accept: boolean }) => workspaceService.answerTransfer(accept),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['team-members'] });
      void queryClient.invalidateQueries({ queryKey: ['team-me'] });
      // The brand left one account and joined another: both plans' brand counts changed.
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Updates the current workspace's profile fields. Server invalidates its own
 * `my-workspaces` Redis cache; we mirror that locally by invalidating the
 * React Query key so the next read picks up the new name/slug/timezone.
 */
export function useUpdateCurrentWorkspace() {
  const queryClient = useQueryClient();
  const setCurrentWorkspace = useWorkspaceStore((s) => s.setCurrentWorkspace);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  return useMutation({
    mutationFn: (input: UpdateWorkspaceInput) => workspaceService.updateCurrent(input),
    onSuccess: (updated) => {
      // Optimistically reflect in the Zustand store so the sidebar updates
      // immediately, without waiting for the network re-fetch.
      if (currentWorkspace) {
        setCurrentWorkspace({ ...currentWorkspace, ...(updated as Partial<Workspace>) });
      }
      queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
    },
  });
}
